
import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import BlogSearch from '@/components/blog/BlogSearch';
import BlogFilters from '@/components/blog/BlogFilters';
import BlogPostGrid from '@/components/blog/BlogPostGrid';
import { getSortedPosts } from '@/services/blogService';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use getSortedPosts instead of manual sorting
  const sortedPosts = getSortedPosts();
  
  // Extract unique categories and tags from the posts
  const allCategories = [...new Set(sortedPosts.map(post => post.category))];
  const allTags = [...new Set(sortedPosts.flatMap(post => post.tags))];

  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Filter posts based on search term, category, and tag
  const filteredPosts = sortedPosts.filter(post => {
    const matchesSearch = searchTerm === '' || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === null || post.category === selectedCategory;
    
    const matchesTag = selectedTag === null || post.tags.includes(selectedTag);
    
    return matchesSearch && matchesCategory && matchesTag;
  });

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(prevCategory => prevCategory === category ? null : category);
    // Reset tag when category changes
    setSelectedTag(null);
  };

  const handleTagClick = (tag: string) => {
    setSelectedTag(prevTag => prevTag === tag ? null : tag);
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden relative">
      <ScrollToTop />
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="blog-vector blog-vector-1 top-40 left-0"></div>
        <div className="blog-vector blog-vector-2 bottom-40 right-0"></div>
        
        <div className="section-container">
          <div className="text-center mb-12">
            <p className="heading-highlight mb-2">My Technical Writing</p>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">Engineering Articles</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A curated collection of engineering essays on React architecture, JavaScript internals, and performance optimization.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row gap-8 mb-12">
            <div className="md:w-3/4">
              <BlogSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
              <BlogPostGrid 
                posts={filteredPosts} 
                isLoading={isLoading} 
                onCategoryClick={handleCategoryClick} 
                onTagClick={handleTagClick}
                selectedTag={selectedTag}
              />
            </div>
            
            <div className="md:w-1/4">
              <BlogFilters 
                categories={allCategories} 
                tags={allTags}
                selectedCategory={selectedCategory}
                selectedTag={selectedTag}
                onCategoryClick={handleCategoryClick}
                onTagClick={handleTagClick}
              />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Blog;
